/* eslint-disable */
import throwAuthCouldNotLoginMockUserError from '../../Errors/Auth/AuthCouldNotLoginMockUser.error';
import throwAuthInvalidHandlerTypeError from '../../Errors/Auth/AuthInvalidHandlerType.error';
import isFunction from '../../Support/Is/Function/IsFunction';

/**
 * MockAuthService
 * @class MockAuthService
 */
class MockAuthService {
  /**
   * The mocked `UserEntity` to use for logging in for testing
   * @type {UserEntity | null}
   * @memberof MockAuthService
   */
  __mockUser = null;

  /**
   * The local representation of the current user
   * @type {UserEntity | null}
   * @memberof MockAuthService
   */
  _user = null;

  /**
   * The `AuthServiceAdapterContract` interface that will be used for connecting to varius auth platforms
   * @type {AuthServiceAdapterContract}
   * @memberof MockAuthService
   */
  authAdapter;

  /**
   * Creates an instance of `MockAuthService`
   * @param {AuthServiceCreateDto} dto The params required to create an instance of `MockAuthService`
   * @memberof MockAuthService
   */
  constructor({ authAdapter }) {
    this.authAdapter = authAdapter;
  }

  /**
   * Login the mocked `UserEntity` provided by `__mockUser`
   * @returns {Promise<AuthEntity>} The auth entity
   * @memberof MockAuthService
   */
  __loginMockUser = () => {
    if (this.__mockUser === null) {
      return throwAuthCouldNotLoginMockUserError();
    }
    /** @type {UserEntity} */
    const user = this.__mockUser;
    const { password, username } = user;
    return this.loginWithUsernameAndPassword({ password, username });
  };

  /**
   * Logout the `UserEntity`
   * @returns {Promise<null>} The updated representation of the current user
   * @memberof MockAuthService
   */
  __logoutMockUser = () => {
    return this.logout();
  };

  /**
   * Get the mock `UserEntity`
   * @returns {UserEntity} The mocked user
   * @memberof MockAuthService
   */
  __getMockUser = () => {
    return this.__mockUser;
  };

  /**
   * Set the mock `UserEntity`
   * @param {UserEntity | null} mockUser The mocked `UserEntity`
   * @returns {void} void
   * @memberof MockAuthService
   */
  __setMockUser = (mockUser) => {
    this.__mockUser = mockUser;
  };

  /**
   * Adds the tokens into the storage. Does not log in the user.
   * @param {any} res The response
   * @param {any} tokenManager The token manager
   * @returns {void} void
   * @memberof MockAuthService
   */
  addTokens = (res, tokenManager) => {
    return this.authAdapter.addTokens(res, tokenManager);
  };

  /**
   * Ensure that the user is logged in and that they have a valid token that has not expired
   * @returns {Headers} The Headers with the included `Authorization` header.
   * @memberof MockAuthService
   */
  createHeaders = () => {
    return this.authAdapter.createHeaders();
  };

  /**
   * Clears auth from storage. Does not log out the user.
   * @returns {void} void
   * @memberof MockAuthService
   */
  clearAuth = () => {
    return this.authAdapter.clearAuth();
  };

  /**
   * Returns the groups
   * @returns {Array<string>} The groups
   * @memberof MockAuthService
   */
  getGroups = () => {
    return [];
  };

  /**
   * Returns the response types for the auth
   * @returns {Array<string>} The response typed
   * @memberof MockAuthService
   */
  getResponseTypes = () => {
    return [];
  };

  /**
   * Returns the scopes for the auth
   * @returns {Array<string>} The scopes
   * @memberof MockAuthService
   */
  getScopes = () => {
    return [];
  };

  /**
   * Returns the user id from auth
   * @returns {string} The user id
   * @memberof MockAuthService
   */
  getUserId = () => {
    return ``;
  };

  /**
   * Determine if the current `auth` has the provided `role`
   * @param {AuthServiceRoles} roleToCheck The `role` to check
   * @returns {boolean} Whether or not the user has the `role`
   * @memberof MockAuthService
   */
  hasRole = (roleToCheck) => {
    return true;
  };

  /**
   * Determine if the current `auth` has **any** of the provided `roles`
   * @param {Array<AuthServiceRoles>} rolesToCheck The `roles` to check
   * @returns {boolean} Whether or not the user has **any** of the `roles`
   * @memberof AuthService
   */
  hasAnyRole = (rolesToCheck) => {
    return true;
  };

  /**
   * Determine if the user is currently authenticated
   * @returns {boolean} Whether or not the current user is authenticated or not
   * @memberof MockAuthService
   */
  isAuthenticated = () => {
    return this.authAdapter.isAuthenticated();
  };

  /**
   * Attempt to login a user
   * @param {AuthServiceLoginWithUsernameAndPasswordDto} dto The required params to login
   * @returns {Promise<AuthEntity>} The auth entity
   * @memberof MockAuthService
   */
  loginWithUsernameAndPassword = async ({ password, username }) => {
    const user = await this.authAdapter.loginWithUsernameAndPassword({ password, username });
    this._user = user;
    return user;
  };

  /**
   * Logout a user
   * @returns {Promise<null>} The updated representation of the current user
   * @memberof MockAuthService
   */
  logout = async () => {
    await this.authAdapter.logout();
    this._user = null;
    return null;
  };

  /**
   * Fire a callback when the auth state changes
   * @param {AuthServiceStateChangedHandler} handler The `handler` to listen to state changes
   * @returns {AuthServiceStateChangedUnsubscribe} The function used to unsubscribe from auth state changes
   * @memberof MockAuthService
   */
  onAuthStateChanged = (handler) => {
    if (!isFunction(handler)) {
      return throwAuthInvalidHandlerTypeError(handler);
    }
    return this.authAdapter.onAuthStateChanged((error, payload) => {
      this._user = (payload && payload.user) || null;
      handler(error, payload);
    });
  };

  /**
   * Update auth service on auth success.
   * Call this when using a 3rd party auth service with a success/fail callback
   * @param {any} response The response from auth
   * @returns {Promise<AuthEntity>} The auth entity
   * @memberof MockAuthService
   */
  onAuthSuccess = (response) => {
    return this.authAdapter.onAuthSuccess(response);
  };
}

export default MockAuthService;
